import { Alert } from '@ksyos/design-system';
import { Fragment, useContext } from 'react';
import type {
    AddCarePathServiceSettingsType,
    IAddCarePathServiceConsumerSettings,
    IAddCarePathServiceProviderSettings,
    IAddCarePathServiceSupervisorSettings,
} from '@common/interfaces/addCarePathServicesSettings';
import { ServiceCode } from '../../../../../../common/enums/serviceCode';
import { ServiceType } from '../../../../../../common/enums/serviceType';
import { translateKey } from '../../../../../translate';
import { settingsList } from '../../../carePath/constants/settingsLists';
import { CareProviderAddRightsContext } from '../careProviderAddRightsContext';
import { requiredServiceConsumerProperties, requiredServiceProviderProperties } from '../defaultSettingsLists';
import { AddPreferredServiceConsumers } from '../settings/AddPreferredServiceConsumers';
import { ConsumerServiceSettings } from '../settings/ConsumerServiceSettings';
import { ServiceSettings } from '../settings/ServiceSettings';

interface IAddSettingsProps {
    employmentStartDate?: string;
    onChange: (settings: AddCarePathServiceSettingsType, errorPath?: string) => void;
}

export function AddSettings(props: IAddSettingsProps) {
    const { dispatchAddRightsAction } = useContext(CareProviderAddRightsContext);

    return (
        <CareProviderAddRightsContext.Consumer>
            {(careProviderAddRightContext) => (
                <>
                    {careProviderAddRightContext.addRightsState.consumerServices.map((consumerService, index) => (
                        <ConsumerServiceSettings
                            employmentStartDate={props.employmentStartDate}
                            errorPrefix={`serviceConsumerSettings.${index}`}
                            errors={careProviderAddRightContext.addRightsState.errors}
                            key={`consumerService-${consumerService.id}`}
                            onChange={handleConsumerSettingsChange}
                            organization={careProviderAddRightContext.addRightsState.organization}
                            practitioner={careProviderAddRightContext.addRightsState.practitioner}
                            providerServices={careProviderAddRightContext.addRightsState.providerServices}
                            requiredProperties={requiredServiceConsumerProperties}
                            service={consumerService}
                            settingsQuestionList={getSettingsQuestionList('consumer', consumerService.serviceType)}
                            title={getTitle(consumerService.name, 'serviceConsumerSettings')}
                        />
                    ))}
                    {careProviderAddRightContext.addRightsState.providerServices.map((providerService, index) => (
                        <Fragment key={`providerService-${providerService.id}-fragment`}>
                            <ServiceSettings<IAddCarePathServiceProviderSettings>
                                errorPrefix={`serviceProviderSettings.${index}`}
                                errors={careProviderAddRightContext.addRightsState.errors}
                                key={`providerService-${providerService.id}`}
                                onChange={handleProviderSettingsChange}
                                requiredProperties={requiredServiceProviderProperties}
                                service={providerService}
                                settingsQuestionList={getSettingsQuestionList('provider', providerService.serviceType)}
                                title={getTitle(providerService.name, 'serviceProviderSettings')}
                            />
                            {providerService.serviceType === ServiceType.examination &&
                                careProviderAddRightContext.addRightsState.errors.has(
                                    `serviceProviderSettings.${index}.analystOrBiometricianOrGrader`
                                ) && (
                                    <div className="has-danger">
                                        <Alert
                                            appearance="warning"
                                            className="mv-0"
                                        >
                                            {providerService.code === ServiceCode.TCOH
                                                ? translateKey(
                                                      'addCarePathSettingsLabelsServiceProvider.oneMustBeTrueWithAnalyst'
                                                  )
                                                : translateKey(
                                                      'addCarePathSettingsLabelsServiceProvider.oneMustBeTrue'
                                                  )}
                                        </Alert>
                                    </div>
                                )}
                        </Fragment>
                    ))}
                    {careProviderAddRightContext.addRightsState.supervisorServices.map((supervisorService, index) => (
                        <ServiceSettings<IAddCarePathServiceSupervisorSettings & { id: string }>
                            errorPrefix={`serviceSupervisorSettings.${index}`}
                            errors={careProviderAddRightContext.addRightsState.errors}
                            key={`supervisorService-${supervisorService.id}`}
                            onChange={handleSupervisorSettingsChange}
                            service={supervisorService}
                            settingsQuestionList={getSettingsQuestionList('supervisor', supervisorService.serviceType)}
                            title={getTitle(supervisorService.name, 'serviceSupervisorSettings')}
                        />
                    ))}
                    {careProviderAddRightContext.addRightsState.providerServices.map((providerService) => (
                        <AddPreferredServiceConsumers
                            hasError={(
                                careProviderAddRightContext.addRightsState.servicesMissingPreferredServiceConsumers ??
                                []
                            ).includes(providerService.id)}
                            key={`preferredServiceConsumers_${providerService.id}`}
                            onChange={(preferredServiceConsumers) =>
                                handlePreferredServiceConsumersChange(preferredServiceConsumers, providerService.id)
                            }
                            providerService={providerService}
                        />
                    ))}
                </>
            )}
        </CareProviderAddRightsContext.Consumer>
    );

    function handlePreferredServiceConsumersChange(preferredServiceConsumers: string[], serviceId: string) {
        dispatchAddRightsAction({ type: 'updatePreferredServiceConsumers', serviceId, preferredServiceConsumers });
    }

    function handleConsumerSettingsChange(settings: IAddCarePathServiceConsumerSettings, errorPath: string) {
        dispatchAddRightsAction({ type: 'updateServiceConsumerSettings', serviceId: settings.id, settings });
        props.onChange(settings, errorPath);
    }

    function handleProviderSettingsChange(settings: IAddCarePathServiceProviderSettings, errorPath: string) {
        dispatchAddRightsAction({ type: 'updateServiceProviderSettings', serviceId: settings.id, settings });
        props.onChange(settings, errorPath);
    }

    function handleSupervisorSettingsChange(
        settings: IAddCarePathServiceSupervisorSettings & { id: string },
        errorPath: string
    ) {
        dispatchAddRightsAction({ type: 'updateServiceSupervisorSettings', serviceId: settings.id, settings });
        props.onChange(settings, errorPath);
    }

    function getTitle(
        serviceName: string,
        settingsType: 'serviceConsumerSettings' | 'serviceProviderSettings' | 'serviceSupervisorSettings'
    ) {
        return `${translateKey(`addCarePathSectionTitles.${settingsType}`)} ${serviceName}`;
    }

    function getSettingsQuestionList(type: 'consumer' | 'provider' | 'supervisor', serviceType: ServiceType) {
        return settingsList()[`${type}_${serviceType}`];
    }
}
